import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import PortfolioAreaList from "@/v2-backoffice-client/portfolio/PortfolioAreaList"
import {
  APP_CODES,
  formatAppCode,
  isMcpAppCode,
  getAppCodeColor,
} from "@/v2-backoffice-client/usage/usage.constants"
import type { UsageAreaDayRow } from "@/v2-backoffice-client/usage/usage.utils"
import { deriveAreaIntelligence } from "@/v2-backoffice-client/portfolio/portfolio.area.utils"
import type { PortfolioCustomerSummary } from "@/v2-backoffice-client/portfolio/portfolio.utils"

export type PortfolioAreaBreakdownProps = ComponentProps<"section"> & {
  /** Fleet daily rows — UNfiltered, the bars show the per-product split. */
  areaDays: UsageAreaDayRow[]
  /** Scoped customers — the adopter/whitespace lists derive from these. */
  customers: PortfolioCustomerSummary[]
}

// Area intelligence: every sellable capability ("bruksområde") with its
// fleet volume split by product, 28-day momentum, and — the sales meat —
// who uses it and which active accounts don't. A capability living mostly
// on Console v1 (amber) is at-risk functionality; a long whitespace list is
// the next campaign. This component derives; the rows and their dialogs
// are PortfolioAreaList, so no click here re-derives the book.
function PortfolioAreaBreakdown(props: PortfolioAreaBreakdownProps) {
  const { areaDays, customers, className, ...sectionProps } = props

  const areas = deriveAreaIntelligence(areaDays, customers)
  const activeCustomerCount = customers.filter(
    (customer) => customer.summary.activeUsers28d > 0,
  ).length
  // Feature-adoption denominator: the fleet's monthly actives.
  const activeUserCount = customers.reduce(
    (sum, customer) => sum + customer.summary.activeUsers28d,
    0,
  )

  // Legend: the consoles always (the split's two constants), plus any MCP
  // server that actually carries volume in the window — a third colour the
  // reader has to be able to name.
  const presentApps = new Set(
    areas.flatMap((area) => area.appSplit.map((split) => split.app)),
  )
  const legendApps = APP_CODES.filter(
    (app) => !isMcpAppCode(app) || presentApps.has(app),
  )

  return (
    <section className={cn("flex flex-col gap-4", className)} {...sectionProps}>
      <PortfolioSectionHeader
        eyebrow="Use cases"
        title="What the whole base uses"
        lead="Every capability with its volume and trend, how many people use it, and the money on each side of it."
        info="Every sellable capability with its weighted 28-day volume (split by product), the previous 28 days as a grey ghost, the daily shape, user-level adoption (share of monthly-active users with it among their top areas — a floor, profiles carry each user's top 3), and adoption priced in money: the 12-month billed revenue of the customers using it, and of the active customers who don't — the value an expansion campaign plays for. 'Using' reads from the same per-user top-areas the playbook's 'Nobody has touched X' plays use, so the two always agree. Click the counts for the full lists."
      >
        <span className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          {legendApps.map((app) => (
            <span key={app} className="flex items-center gap-1">
              <span
                className="size-2 rounded-full"
                style={{ backgroundColor: getAppCodeColor(app) }}
              />
              {formatAppCode(app)}
            </span>
          ))}
          <span>weighted usage actions · last 28 d vs previous (grey)</span>
        </span>
      </PortfolioSectionHeader>

      <UsageReportCard className="flex-1">
        <PortfolioAreaList
          areas={areas}
          activeCustomerCount={activeCustomerCount}
          activeUserCount={activeUserCount}
        />
      </UsageReportCard>
    </section>
  )
}

export default PortfolioAreaBreakdown
